// src/pages/SetPassword.jsx
import React, { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import API from '../api';
import { clearAuth } from '../utils/auth';
import Button from '../components/common/Button';
import { TextInput } from '../components/common/form';

const getTokenFallback = () => {
    try {
        const url = new URL(window.location.href);
        const qsToken = url.searchParams.get('token');
        if (qsToken) return qsToken;
        const hash = window.location.hash || '';
        const parts = hash.split('/');
        const maybeToken = parts[parts.length - 1];
        if (maybeToken && maybeToken.length > 20) return maybeToken;
        return '';
    } catch {
        return '';
    }
};

const SetPassword = () => {
    const { t } = useTranslation();

    useEffect(() => {
        clearAuth(true);
    }, []);

    const { token: paramToken } = useParams();
    const navigate = useNavigate();

    const routeToken = useMemo(() => (paramToken || '').trim(), [paramToken]);
    const fallbackToken = useMemo(() => getTokenFallback().trim(), []);
    const effectiveToken = routeToken || fallbackToken;

    const passwordPattern = /^(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&]).{8,}$/;

    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const submit = async (e) => {
        e.preventDefault();

        if (!effectiveToken) {
            alert(t('authPages.invalidToken'));
            return;
        }

        if (!passwordPattern.test(password)) {
            alert(t('authPages.passwordInvalid'));
            return;
        }
        if (password !== confirm) {
            alert(t('authPages.passwordsMismatch'));
            return;
        }

        try {
            setSubmitting(true);
            await API.post('/auth/set-password', { token: effectiveToken, password });
            alert(t('authPages.passwordSetSuccess'));
            navigate('/login');
        } catch (err) {
            console.error('set-password failed:', err);
            alert(t('authPages.linkInvalidExpired'));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="max-w-md mx-auto bg-white p-6 rounded shadow">
            <h2 className="page-hero-title mb-4">{t('authPages.setPasswordTitle')}</h2>
            {!effectiveToken && (
                <p className="text-sm text-red-600 mb-3">
                    {t('authPages.missingToken')}
                </p>
            )}
            <form onSubmit={submit} className="space-y-3">
                <TextInput
                    label={t('authPages.newPassword')}
                    type="password"
                    placeholder={t('authPages.newPassword')}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={8}
                />
                <TextInput
                    label={t('authPages.confirmPassword')}
                    type="password"
                    placeholder={t('authPages.confirmPassword')}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                    minLength={8}
                />
                <p className="text-xs text-gray-500">
                    {t('authPages.passwordRules')}
                </p>
                <Button
                    type="submit"
                    variant="primary"
                    className="w-full"
                    disabled={submitting}
                >
                    {submitting ? t('authPages.saving') : t('authPages.setPasswordButton')}
                </Button>
            </form>
        </div>
    );
};

export default SetPassword;
